// 3_pages/ordering_cart.js
// Ordering process pages (migrated from the old order.js as a whole): cart page
// (body.id--9) sidebar/gift edits and custom gift selection; shipping+payment
// step (id--16) method gating, Christmas-package warnings and recap rearranging;
// customer-details step (id--17) address box regrouping; thank-you page (id--15)
// summary placement.

import { byShop } from "../0_global/shop_map.js";
import { getDeliveryEtaText, loadPublicHolidays } from "../1_utils/delivery_date.js";

export function init() {
	const body = document.body;

	if (body.classList.contains("id--9")) {
		sidebarEdit();
		giftEdit();
		giftSelectCustom();
		storeSelectedGiftImage();
		enhanceCartItemDiscounts();

		document.addEventListener("ShoptetDOMContentLoaded", function () {
			sidebarEdit();
			giftEdit();
			giftSelectCustom();
			storeSelectedGiftImage();
			enhanceCartItemDiscounts();
		});
		document.addEventListener("ShoptetDOMCartContentLoaded", function () {
			storeSelectedGiftImage();
			enhanceCartItemDiscounts();
		});
		document.addEventListener("ShoptetCartUpdated", enhanceCartItemDiscounts);
	}

	if (body.classList.contains("id--16")) {
		const deliveryMethodWrapper = document.querySelector(".co-delivery-method");
		const paymentMethodWrapper = document.querySelector(".co-payment-method");

		refreshShippingDeliveryDates();
		document.addEventListener("ShoptetDOMContentLoaded", refreshShippingDeliveryDates);
		document.addEventListener("ShoptetShippingMethodUpdated", refreshShippingDeliveryDates);

		document.addEventListener("DOMContentLoaded", function () {
			// Always clear Shoptet defaults so nothing is preselected on a fresh visit.
			// Then restore only methods the user already chose in this tab (sessionStorage),
			// so a failed "Continue" (missing payment / pickup) does not wipe shipping again.
			if (deliveryMethodWrapper) {
				disableInputs(deliveryMethodWrapper);
				removeDeliveryFromRecap();
			}
			if (paymentMethodWrapper) {
				paymentMethodWrapper.classList.add("disabled");
				disableInputs(paymentMethodWrapper);
				removePaymentFromRecap();
			}
			checkVanocniBalicky();
			refreshShippingDeliveryDates();
			bindCheckoutMethodPersistence(deliveryMethodWrapper, paymentMethodWrapper);
			restoreCheckoutShippingAndPayment(deliveryMethodWrapper, paymentMethodWrapper);
		});

		rearangeRecap();

		fetchImagesOfProductsInCart();
		insertStoredGiftImage();
	}

	if (body.classList.contains("id--17")) {
		rearangeRecap();
		fetchImagesOfProductsInCart();
		saveCheckoutEmailOnSubmit();
		insertStoredGiftImage();
		saveCheckoutEmailOnSubmit();

		try {
			const boxContact = document.querySelector(".co-contact-information");
			const boxBilling = document.querySelector(".co-billing-address");
			const boxDelivery = document.querySelector(".co-shipping-address");
			const boxAdditional = document.querySelector(".co-box-additional");
			const companyShopping = document.querySelector(".company-shopping");

			if (boxBilling && companyShopping) {
				boxBilling.appendChild(companyShopping);
			}

			if (boxBilling && boxDelivery) {
				const anotherShippingElem = boxDelivery.querySelector("#another-shipping");
				if (anotherShippingElem?.parentElement) {
					boxBilling.appendChild(anotherShippingElem.parentElement);
				}
				const billingAddressH4 = boxBilling.querySelector(".header-billing");
				if (billingAddressH4) {
					billingAddressH4.textContent = window.NAT.translations("cartBillingDeliveryAddress");
				}
			}

			if (boxContact && boxAdditional) {
				boxContact.appendChild(boxAdditional);
			}
		} catch (error) {
			console.error("There has been a problem with your query:", error);
		}
	}

	if (body.classList.contains("id--15")) {
		clearCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY);
		clearCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
		thxOrder();
		passCheckoutDataToTally();
	}
}

function insertShippingMethodsHeader(container) {
	const deliveryBox = container.closest(".co-delivery-method");
	if (!deliveryBox) return;

	let header = deliveryBox.querySelector(".shipping-methods-header");
	if (!header) {
		header = document.createElement("div");
		header.className = "shipping-methods-header";
		header.setAttribute("aria-hidden", "true");

		const when = document.createElement("span");
		when.className = "shipping-methods-header__when";
		when.textContent = window.NAT.translations("cartDeliveryWhen");

		const price = document.createElement("span");
		price.className = "shipping-methods-header__price";
		price.textContent = window.NAT.translations("cartPriceWithVat");

		header.append(when, price);
	}

	let row = deliveryBox.querySelector(".shipping-methods-heading-row");
	if (!row) {
		row = document.createElement("div");
		row.className = "shipping-methods-heading-row";
		const heading = deliveryBox.querySelector("h4, h2.h4, h2");
		if (heading) {
			heading.before(row);
			row.append(heading);
		} else {
			container.before(row);
		}
	}

	if (header.parentElement !== row) {
		row.append(header);
	}
}

function applyShippingDeliveryDates(container, holidays) {
	container.querySelectorAll(".radio-wrapper").forEach((row) => {
		const label = row.querySelector("label");
		if (!label) return;
		const code = row.querySelector("input[data-code]")?.getAttribute("data-code") || "";
		const text = getDeliveryEtaText({
			isZasilkovna: isZasilkovnaPickupPoint(row, code),
			isPersonalPickup: code === "personal-collection",
			holidays,
		});
		let eta = label.querySelector(".delivery-eta");
		if (!eta) {
			eta = document.createElement("span");
			eta.className = "delivery-eta";
			const price = label.querySelector(".payment-shipping-price");
			if (price) {
				price.before(eta);
			} else {
				label.append(eta);
			}
		}
		eta.textContent = text;
	});
}

function isZasilkovnaPickupPoint(row, code) {
	if (code !== "zasilkovna") return false;
	return Boolean(row.querySelector("input.zasilkovna-choose-branch, input.choose-branch, [data-testid='deliveryPoint']"));
}

let shippingHolidays = new Set();

function refreshShippingDeliveryDates() {
	const container = document.querySelector("#order-shipping-methods");
	if (!container) return;
	insertShippingMethodsHeader(container);
	applyShippingDeliveryDates(container, shippingHolidays);
	loadPublicHolidays().then((holidays) => {
		shippingHolidays = holidays;
		applyShippingDeliveryDates(container, holidays);
	});
}

function parseCartPrice(text) {
	if (!text) return NaN;
	const thou = window.shoptet?.config?.thousandSeparator ?? " ";
	const dec = window.shoptet?.config?.decSeparator ?? ",";
	let raw = String(text).replace(/[^\d.,\s\u00a0-]/g, "");
	if (thou) raw = raw.split(thou).join("");
	raw = raw.replace(/[\s\u00a0]/g, "");
	if (dec && dec !== ".") raw = raw.replace(dec, ".");
	return parseFloat(raw);
}

function formatCartPrice(value) {
	if (typeof value === "number" && typeof value.ShoptetFormatAsCurrency === "function") {
		return value.ShoptetFormatAsCurrency();
	}
	return String(value);
}

function disableCartPriceTooltip(tooltip) {
	const $el = window.jQuery?.(tooltip);
	if ($el?.data("bs.tooltip")) {
		$el.tooltip("destroy");
	}
	tooltip.removeAttribute("title");
	tooltip.classList.remove("show-tooltip");
}

function hideZeroCartDiscount(row) {
	const discountCell = row.querySelector(".p-discount");
	if (!discountCell) return;

	const discountEl =
		discountCell.querySelector('[data-testid="cartItemDiscount"]') || discountCell.querySelector(".show-tooltip.acronym");
	if (!discountEl) return;

	const percentMatch = discountEl.textContent.match(/(\d+(?:[.,]\d+)?)\s*%/);
	const percent = percentMatch ? parseCartPrice(percentMatch[1]) : NaN;
	discountEl.hidden = !Number.isFinite(percent) || percent <= 0;
}

function enhanceCartItemDiscounts() {
	document.querySelectorAll(".cart-table tr.removeable").forEach(function (row) {
		hideZeroCartDiscount(row);

		const priceCell = row.querySelector(".p-price");
		const tooltip = priceCell?.querySelector('[data-testid="cartItemDiscount"]') || priceCell?.querySelector(".show-tooltip.acronym");
		const priceFinal = priceCell?.querySelector(".price-final");
		row.querySelector(".p-total .cart-item-discount")?.remove();
		if (!tooltip || !priceCell || !priceFinal) {
			priceCell?.querySelector(".cart-item-discount")?.remove();
			return;
		}

		const tooltipText = tooltip.getAttribute("data-original-title") || tooltip.getAttribute("title") || "";
		disableCartPriceTooltip(tooltip);
		const percentMatch = tooltipText.match(/(\d+(?:[.,]\d+)?)\s*%/);
		const withoutPercent = tooltipText.replace(/(\d+(?:[.,]\d+)?)\s*%/g, "");
		const originalUnitMatch = withoutPercent.match(/(\d[\d\s\u00a0.,]*)/);
		const originalUnit = originalUnitMatch ? parseCartPrice(originalUnitMatch[1]) : NaN;
		const currentUnit = parseCartPrice(priceFinal.textContent);
		if (!percentMatch || !Number.isFinite(originalUnit) || originalUnit <= 0 || !Number.isFinite(currentUnit)) {
			priceCell.querySelector(".cart-item-discount")?.remove();
			return;
		}

		if (originalUnit <= currentUnit) {
			priceCell.querySelector(".cart-item-discount")?.remove();
			return;
		}

		const percent = Math.round(parseCartPrice(percentMatch[1]));
		let priceWrap = priceCell.querySelector(".cart-item-price-wrap");
		if (!priceWrap) {
			const priceGroup = priceFinal.closest('[data-testid="cartItemDiscount"]') || priceFinal.closest(".acronym") || priceFinal;
			const unitValue = priceCell.querySelector(".unit-value");
			priceWrap = document.createElement("span");
			priceWrap.className = "cart-item-price-wrap";
			priceGroup.after(priceWrap);
			priceWrap.append(priceGroup);
			if (unitValue) priceWrap.append(unitValue);
		}
		let discountRow = priceCell.querySelector(".cart-item-discount");
		if (!discountRow) {
			discountRow = document.createElement("span");
			discountRow.className = "cart-item-discount";
			const originalEl = document.createElement("span");
			originalEl.className = "cart-item-discount__original";
			const percentEl = document.createElement("span");
			percentEl.className = "cart-item-discount__percent";
			discountRow.append(originalEl, percentEl);
			priceWrap.before(discountRow);
		}
		discountRow.querySelector(".cart-item-discount__original").textContent = formatCartPrice(originalUnit);
		discountRow.querySelector(".cart-item-discount__percent").textContent = "-" + percent + "%";
	});
}

function sidebarEdit() {
	const sidebarInCart = document.querySelector(".sidebar-in-cart");
	if (!sidebarInCart) return;

	const discountCoupon = document.querySelector(".discount-coupon");
	if (discountCoupon) {
		sidebarInCart.prepend(discountCoupon);
	}

	const deliveryTime = document.querySelector(".delivery-time");
	if (deliveryTime) {
		sidebarInCart.prepend(deliveryTime);
	}

	const extraDiscount = document.querySelector(".extra.discount");
	if (extraDiscount) {
		sidebarInCart.prepend(extraDiscount);
	}

	const extraDelivery = document.querySelector(".extra.delivery");
	if (extraDelivery) {
		sidebarInCart.prepend(extraDelivery);
	}
}

function giftEdit() {
	const freeGift = document.querySelector(".free-gift");
	if (!freeGift) return;

	const giftHeader = document.querySelector(".cart-summary .h4");
	if (giftHeader) {
		giftHeader.textContent = window.NAT.translations("cartGiftsTitle");
	}

	const extraGift = document.querySelector(".extra.gift");
	if (!extraGift) {
		return;
	}
	freeGift.prepend(extraGift);

	const giftSpan = document.querySelector(".extra.gift > span");
	if (giftSpan) {
		const darkyPrice = document.querySelector(".extra.gift > span > strong")?.textContent ?? "";
		giftSpan.innerHTML = window.NAT.translations("cartGiftOrderMore").replace("{price}", darkyPrice);
	}
}

function giftSelectCustom() {
	document.querySelectorAll(".free-gifts-wrapper .free-gifts label").forEach((label) => {
		label.addEventListener("click", function (event) {
			event.preventDefault();
			const forId = label.getAttribute("for");
			document.querySelectorAll(".free-gifts input").forEach((input) => {
				input.checked = input.id === forId;
			});
			const form = document.querySelector(".free-gifts-wrapper form");
			if (form && window.shoptet && shoptet.cart && typeof shoptet.cart.ajaxSubmitForm === "function") {
				shoptet.cart.ajaxSubmitForm(form.getAttribute("action"), form, "functionsForCart", "cart", true);
			}
			storeSelectedGiftImage();
		});
	});
}

const SELECTED_GIFT_STORAGE_KEY = "natSelectedGift";

function normalizeGiftName(name) {
	return name?.replace(/\s+/g, " ").trim() || "";
}

function extractGiftFromItem(item, selectedInput) {
	if (!item) return null;
	const img = item.querySelector(".free-gifts-img-hover img") || item.querySelector(".free-gifts-img img");
	if (!img) return null;
	return {
		code: item.getAttribute("data-free-gift-id") || selectedInput?.value || "",
		src: img.getAttribute("data-src") || img.currentSrc || img.src,
		alt: img.alt || "",
		name: normalizeGiftName(item.querySelector(".free-gift-name")?.textContent),
	};
}

function extractSelectedGift(root) {
	const form = root.querySelector(".free-gifts-wrapper form");
	if (!form) return null;
	const selectedInput = form.querySelector('input[name="codeId"]:checked');
	const selectedItem = selectedInput?.closest("li") || form.querySelector("li.active");
	return extractGiftFromItem(selectedItem, selectedInput);
}

function storeSelectedGiftImage() {
	const gift = extractSelectedGift(document);
	if (!gift) {
		if (document.querySelector(".free-gifts-wrapper form")) {
			localStorage.removeItem(SELECTED_GIFT_STORAGE_KEY);
		}
		return;
	}
	localStorage.setItem(SELECTED_GIFT_STORAGE_KEY, JSON.stringify(gift));
}

function readStoredGift() {
	try {
		return JSON.parse(localStorage.getItem(SELECTED_GIFT_STORAGE_KEY));
	} catch (error) {
		return null;
	}
}

function getKosikFetchUrl() {
	const kosikUrl = window.NAT.translations("kosikUrl");
	try {
		const url = new URL(kosikUrl, window.location.origin);
		if (url.origin === window.location.origin) return url.href;
		return url.pathname;
	} catch (error) {
		return kosikUrl;
	}
}

async function fetchSelectedGiftFromCart() {
	const kosikUrl = getKosikFetchUrl();
	if (!kosikUrl) return null;
	try {
		const response = await fetch(kosikUrl);
		if (!response.ok) throw new Error("Network response was not ok");
		const html = await response.text();
		const doc = new DOMParser().parseFromString(html, "text/html");
		return extractSelectedGift(doc);
	} catch (error) {
		console.error("There has been a problem with your fetch operation:", error);
		return null;
	}
}

function prependGiftImage(itemName, gift, recapName) {
	if (itemName.querySelector(".image-block")) return;
	const imageBlock = document.createElement("div");
	imageBlock.classList.add("image-block");
	const img = document.createElement("img");
	img.src = gift.src;
	img.alt = gift.alt || recapName || "";
	imageBlock.appendChild(img);
	itemName.prepend(imageBlock);
}

async function insertStoredGiftImage() {
	const giftNames = document.querySelectorAll(".cart-item.cart-item-gift .cart-item-name");
	if (!giftNames.length) return;

	let stored = readStoredGift();
	if (!stored?.src) {
		stored = await fetchSelectedGiftFromCart();
		if (stored?.src) {
			localStorage.setItem(SELECTED_GIFT_STORAGE_KEY, JSON.stringify(stored));
		}
	}
	if (!stored?.src) return;

	const storedName = normalizeGiftName(stored.name);

	giftNames.forEach((itemName) => {
		const recapName = normalizeGiftName(itemName.querySelector("[data-testid='recapItemName']")?.textContent);
		if (storedName && recapName && storedName !== recapName && giftNames.length > 1) return;
		prependGiftImage(itemName, stored, recapName);
	});
}

function disableInputs(method) {
	const allInputs = method.querySelectorAll("input");

	if (!allInputs) return;
	allInputs.forEach((input) => {
		input.checked = false;
		input.parentElement.classList.remove("active");
	});
}

const CHECKOUT_SHIPPING_STORAGE_KEY = "natCheckoutShippingMethod";
const CHECKOUT_PAYMENT_STORAGE_KEY = "natCheckoutPaymentMethod";

function getActiveMethodRadio(containerSelector) {
	return (
		document.querySelector(`${containerSelector} > .radio-wrapper.active input[type="radio"]`) ||
		document.querySelector(`${containerSelector} input[type="radio"]:checked`)
	);
}

function saveCheckoutMethod(storageKey, containerSelector) {
	const input = getActiveMethodRadio(containerSelector);
	if (!input) return;

	const row = input.closest(".radio-wrapper");
	const branch = row?.querySelector(
		"input.zasilkovna-choose-branch, input.choose-branch, input[data-testid='deliveryPoint']",
	);

	const payload = {
		id: input.id || "",
		value: input.value || "",
		branchValue: branch?.value || "",
	};

	try {
		sessionStorage.setItem(storageKey, JSON.stringify(payload));
	} catch (error) {
		console.warn("Could not save checkout method selection:", error);
	}
}

function clearCheckoutMethod(storageKey) {
	try {
		sessionStorage.removeItem(storageKey);
	} catch (error) {
		console.warn("Could not clear checkout method selection:", error);
	}
}

function readCheckoutMethod(storageKey) {
	try {
		const raw = sessionStorage.getItem(storageKey);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch (error) {
		console.warn("Could not read checkout method selection:", error);
		return null;
	}
}

function restoreCheckoutMethod(storageKey, containerSelector, { quiet = false } = {}) {
	const payload = readCheckoutMethod(storageKey);
	if (!payload) return false;

	const container = document.querySelector(containerSelector);
	if (!container) return false;

	let input = payload.id ? document.getElementById(payload.id) : null;
	if (!input && payload.value) {
		input = container.querySelector(`input[type="radio"][value="${CSS.escape(payload.value)}"]`);
	}
	if (!input || !container.contains(input)) return false;

	const row = input.closest(".radio-wrapper");
	if (payload.branchValue) {
		const branch = row?.querySelector(
			"input.zasilkovna-choose-branch, input.choose-branch, input[data-testid='deliveryPoint']",
		);
		if (branch) {
			branch.value = payload.branchValue;
		}
	}

	input.checked = true;
	row?.classList.add("active");
	if (!quiet) {
		input.dispatchEvent(new Event("change", { bubbles: true }));
	}
	return true;
}

let ignorePaymentAutoClearUntil = 0;

function clearPaymentSelection(paymentMethodWrapper) {
	clearCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
	removePaymentFromRecap();
	if (paymentMethodWrapper) {
		disableInputs(paymentMethodWrapper);
	}
}

function reassertStoredShippingQuietly() {
	const payload = readCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY);
	if (!payload) return;

	const container = document.querySelector("#order-shipping-methods");
	if (!container) return;

	let input = payload.id ? document.getElementById(payload.id) : null;
	if (!input && payload.value) {
		input = container.querySelector(`input[type="radio"][value="${CSS.escape(payload.value)}"]`);
	}
	if (!input || !container.contains(input)) return;

	const row = input.closest(".radio-wrapper");
	if (payload.branchValue) {
		const branch = row?.querySelector(
			"input.zasilkovna-choose-branch, input.choose-branch, input[data-testid='deliveryPoint']",
		);
		if (branch) {
			branch.value = payload.branchValue;
		}
	}

	input.checked = true;
	row?.classList.add("active");
	document.querySelector(".co-delivery-method")?.classList.add("selected");
}

function ensureNoDefaultPayment(paymentMethodWrapper) {
	if (Date.now() < ignorePaymentAutoClearUntil) return;
	if (readCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY)) return;
	clearPaymentSelection(paymentMethodWrapper);
	paymentMethodWrapper?.classList.remove("disabled");
	reassertStoredShippingQuietly();
}

function restoreCheckoutShippingAndPayment(deliveryMethodWrapper, paymentMethodWrapper) {
	const restoredShipping = restoreCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY, "#order-shipping-methods");
	if (!restoredShipping) return;

	if (deliveryMethodWrapper) {
		deliveryMethodWrapper.classList.add("selected");
	}
	if (paymentMethodWrapper) {
		paymentMethodWrapper.classList.remove("disabled");
	}

	const hasStoredPayment = Boolean(readCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY));

	const applyPaymentAfterShipping = () => {
		if (hasStoredPayment) {
			restoreCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods");
			return;
		}
		ensureNoDefaultPayment(paymentMethodWrapper);
	};
	setTimeout(applyPaymentAfterShipping, 300);
	setTimeout(applyPaymentAfterShipping, 600);
}

function syncCheckoutShippingUi(deliveryMethodWrapper, paymentMethodWrapper) {
	const activeDeliveryMethod =
		document.querySelector("#order-shipping-methods > .radio-wrapper.active") ||
		getActiveMethodRadio("#order-shipping-methods")?.closest(".radio-wrapper");

	if (activeDeliveryMethod) {
		const previousShipping = readCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY);
		const currentShipping = getActiveMethodRadio("#order-shipping-methods");
		const shippingChanged = previousShipping && currentShipping && previousShipping.value !== currentShipping.value;
		const shippingFirstSelect = !previousShipping && Boolean(currentShipping);
		saveCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY, "#order-shipping-methods");
		if (shippingChanged || shippingFirstSelect) {
			clearCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
			ignorePaymentAutoClearUntil = 0;
			setTimeout(() => {
				ensureNoDefaultPayment(paymentMethodWrapper);
			}, 200);
		}
		paymentMethodWrapper?.classList.remove("disabled");
		deliveryMethodWrapper?.classList.add("selected");
	} else {
		deliveryMethodWrapper?.classList.remove("selected");
		clearCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY);
	}
}

function syncCheckoutPaymentUi(deliveryMethodWrapper, paymentMethodWrapper, { userInitiated = false } = {}) {
	const activeDeliveryMethod =
		document.querySelector("#order-shipping-methods > .radio-wrapper.active") ||
		getActiveMethodRadio("#order-shipping-methods")?.closest(".radio-wrapper");
	const activePaymentMethod =
		document.querySelector("#order-billing-methods > .radio-wrapper.active") ||
		getActiveMethodRadio("#order-billing-methods")?.closest(".radio-wrapper");

	if (userInitiated && activePaymentMethod) {
		saveCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods");
		ignorePaymentAutoClearUntil = Date.now() + 1200;
		if (activeDeliveryMethod) {
			paymentMethodWrapper?.classList.remove("disabled");
			deliveryMethodWrapper?.classList.add("selected");
		}
		return;
	}

	const storedPayment = readCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
	const inPaymentLock = Date.now() < ignorePaymentAutoClearUntil;

	if (activePaymentMethod) {
		if (!storedPayment) {
			if (inPaymentLock) {
				saveCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods");
			} else {
				setTimeout(() => {
					ensureNoDefaultPayment(paymentMethodWrapper);
				}, 0);
			}
		}
	} else if (storedPayment) {
		setTimeout(() => {
			const restored = restoreCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods", {
				quiet: true,
			});
			if (!restored && Date.now() >= ignorePaymentAutoClearUntil) {
				clearCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
				removePaymentFromRecap();
				if (paymentMethodWrapper) {
					disableInputs(paymentMethodWrapper);
				}
			}
			reassertStoredShippingQuietly();
		}, 200);
		if (inPaymentLock) {
			setTimeout(() => {
				restoreCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods", { quiet: true });
				reassertStoredShippingQuietly();
			}, 500);
		}
	} else {
		setTimeout(() => {
			removePaymentFromRecap();
			if (paymentMethodWrapper) {
				disableInputs(paymentMethodWrapper);
			}
			reassertStoredShippingQuietly();
		}, 200);
	}

	if (activeDeliveryMethod) {
		paymentMethodWrapper?.classList.remove("disabled");
		deliveryMethodWrapper?.classList.add("selected");
	} else {
		deliveryMethodWrapper?.classList.remove("selected");
	}
}

function bindCheckoutMethodPersistence(deliveryMethodWrapper, paymentMethodWrapper) {
	document.addEventListener("ShoptetShippingMethodUpdated", () => {
		syncCheckoutShippingUi(deliveryMethodWrapper, paymentMethodWrapper);
	});
	document.addEventListener("ShoptetBillingMethodUpdated", () => {
		syncCheckoutPaymentUi(deliveryMethodWrapper, paymentMethodWrapper, { userInitiated: false });
	});

	document.querySelector("#order-shipping-methods")?.addEventListener("change", (event) => {
		if (!(event.target instanceof HTMLInputElement) || event.target.type !== "radio") return;
		syncCheckoutShippingUi(deliveryMethodWrapper, paymentMethodWrapper);
	});

	const billingMethods = document.querySelector("#order-billing-methods");
	billingMethods?.addEventListener(
		"pointerdown",
		(event) => {
			if (!(event.target instanceof Element)) return;
			if (!event.target.closest(".radio-wrapper, label, input[type='radio']")) return;
			ignorePaymentAutoClearUntil = Date.now() + 1200;
		},
		true,
	);
	billingMethods?.addEventListener("change", (event) => {
		if (!(event.target instanceof HTMLInputElement) || event.target.type !== "radio") return;
		syncCheckoutPaymentUi(deliveryMethodWrapper, paymentMethodWrapper, { userInitiated: event.isTrusted });
	});
}

function removeDeliveryFromRecap() {
	const recapText = document.querySelector(".recapitulation-shipping-billing .recapitulation-shipping-billing-info");
	if (recapText) {
		recapText.textContent = window.NAT.translations("cartChooseShipping");
	}
}

function removePaymentFromRecap() {
	const recapText = document.querySelector(
		".recapitulation-shipping-billing.last .recapitulation-shipping-billing-info",
	);
	if (recapText) {
		recapText.textContent = window.NAT.translations("cartChoosePayment");
	}
}

function checkVanocniBalicky() {
	const vanocniBalickyKody = [
		"VB-001",
		"VB-002",
		"VB-003",
		"VB-004",
		"VB-005",
		"VB-006",
		"VB-007",
		"VB-008",
		"VB-009",
		"VB-010",
		"VB-011",
		"VB-012",
		"VB-013",
		"VB-014",
		"VB-015",
		"VB-016",
		"VB-017",
		"VB-018",
		"VB-019",
		"VB-020",
		"VBK-AZPM",
		"VBK-BK",
		"VBK-CK",
		"VBK-EC",
		"VBK-EDPM",
		"VBK-CHPS",
		"VBK-KR",
		"VBK-LP",
		"VBK-PKK",
		"VBK-POAVV",
		"VBK-PSAZV",
		"VBK-RK",
		"VBK-SSPM",
		"VBK-TEPM",
	];

	let pocetVanocnichBalicku = 0;
	// Get all cart-item elements
	const cartItems = document.querySelectorAll(".cart-item");

	cartItems.forEach((item) => {
		const sku = item.getAttribute("data-micro-sku");
		if (vanocniBalickyKody.includes(sku)) {
			const amountElem = item.querySelector(".cart-item-amount");
			if (amountElem) {
				const amount = parseInt(amountElem.textContent, 10);
				if (!isNaN(amount)) {
					pocetVanocnichBalicku += amount;
				}
			}
		}
	});

	console.log("Celkem vánočních balíčků:", pocetVanocnichBalicku);
	if (pocetVanocnichBalicku > 2) {
		if (window.NAT.lang === "cs") {
			const zasilkovnaElement = document.querySelector("#shipping-32 .payment-info");
			if (zasilkovnaElement) {
				const warningMessage = document.createElement("p");
				warningMessage.innerHTML = window.NAT.translations("cartChristmasZboxWarning");

				warningMessage.classList.add("warning-message");
				zasilkovnaElement.appendChild(warningMessage);
			}

			const dpdPickupElement = document.querySelector("#shipping-35 .payment-info");
			if (dpdPickupElement) {
				const warningMessage = document.createElement("p");
				warningMessage.innerHTML = window.NAT.translations("cartChristmasPickupBoxWarning");
				warningMessage.classList.add("warning-message");
				dpdPickupElement.appendChild(warningMessage);
			}

			const ceskaPostaElement = document.querySelector("#shipping-48 .payment-info");
			if (ceskaPostaElement) {
				const warningMessage = document.createElement("p");
				warningMessage.innerHTML = window.NAT.translations("cartChristmasPickupBoxWarning");
				warningMessage.classList.add("warning-message");
				ceskaPostaElement.appendChild(warningMessage);
			}
		}

		if (window.NAT.lang === "sk") {
			// NOTE: the old code assigned this without declaring it (implicit global).
			const zasilkovnaElement = document.querySelector("#shipping-17 .payment-info");
			if (zasilkovnaElement) {
				const warningMessage = document.createElement("p");
				warningMessage.innerHTML = window.NAT.translations("cartChristmasZboxWarning");
				warningMessage.classList.add("warning-message");
				zasilkovnaElement.appendChild(warningMessage);
			}
			const slovenskaPostaBoxyElement = document.querySelector("#shipping-95");
			if (slovenskaPostaBoxyElement) {
				slovenskaPostaBoxyElement.classList.add("display-none");
			}
		}
		if (window.NAT.lang === "pl") {
			// NOTE: the old code assigned this without declaring it (implicit global).
			const paczkomatyElement = document.querySelector("#shipping-239 .payment-info");
			if (paczkomatyElement) {
				const warningMessage = document.createElement("p");
				warningMessage.innerHTML = window.NAT.translations("cartChristmasPaczkomatWarning");
				warningMessage.classList.add("warning-message");
				paczkomatyElement.appendChild(warningMessage);
			}
		}
	}
}

function rearangeRecap() {
	const checkoutSidebar = document.querySelector("#checkoutSidebar");
	if (!checkoutSidebar) return;

	// NOTE: recapTitle and cartItems were undeclared (implicit globals) in the old code.
	const recapTitle = checkoutSidebar.querySelector("h2");
	const cartItems = checkoutSidebar.querySelectorAll(".cart-items");
	const recapWrapper = document.createElement("div");
	recapWrapper.classList.add("recap-wrapper");
	if (recapTitle) {
		recapWrapper.appendChild(recapTitle);
	}

	if (cartItems) {
		cartItems.forEach((item) => {
			if (item.querySelector(".recapitulation-single")) {
				const kuponText = window.NAT.translations("cartCouponKeyword");
				if (!item.querySelector(".recapitulation-single").textContent.toUpperCase().includes(kuponText)) {
					return;
				} else {
					const summaryInner = document.querySelector("#checkoutSidebar .order-summary-inner");
					summaryInner.prepend(item.querySelector(".recapitulation-single"));
				}
			}
			recapWrapper.appendChild(item);
		});
		checkoutSidebar.insertAdjacentElement("beforeBegin", recapWrapper);
	}
}

async function fetchImagesOfProductsInCart() {
	const itemNames = document.querySelectorAll(".cart-item .cart-item-name");
	if (!itemNames) return;

	itemNames.forEach(async (itemName) => {
		const productLink = itemName.querySelector("a");
		if (!productLink) return;

		const imageBlock = document.createElement("div");
		imageBlock.classList.add("image-block");
		itemName.prepend(imageBlock);

		const productUrl = productLink.href;

		try {
			const response = await fetch(productUrl);

			if (!response.ok) throw new Error("Network response was not ok");

			const html = await response.text();

			// Parse the HTML string into a document
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, "text/html");

			// Get the image element
			const img = doc.querySelector(".p-main-image > img");

			if (img) {
				// Clone the image so it can be used in the current DOM
				const newImg = document.createElement("img");
				newImg.src = img.src.replace("/big/", "/detail/");
				newImg.alt = img.alt || "";

				// Prepend to itemName
				itemName.querySelector(".image-block").append(newImg);
			}
		} catch (error) {
			console.error("There has been a problem with your fetch operation:", error);
		}
	});
}

function thxOrder() {
	const thxElement = document.querySelector(".summary-thx");
	if (!thxElement) return;

	const h1Element = document.querySelector("h1");
	if (h1Element) {
		h1Element.insertAdjacentElement("beforebegin", thxElement);
	}
}

const CHECKOUT_EMAIL_STORAGE_KEY = "natimaCheckoutEmail";
const TALLY_THANK_YOU_FORM_IDS = {
	cs: "Y5xDvJ",
	cs_clone: "Y5xDvJ",
	sk: "",
	pl: "",
};
const TALLY_HIDDEN_ORDER_EMAIL = "order_email";
const TALLY_HIDDEN_ORDER_ID = "order_id";

function getThankYouTallyFormId() {
	return byShop(TALLY_THANK_YOU_FORM_IDS) || "";
}

function getCheckoutEmailInput() {
	return (
		document.querySelector('.co-contact-information input[name="email"]') ||
		document.querySelector("#email") ||
		document.querySelector('input[type="email"][name="email"]')
	);
}

function saveCheckoutEmailOnSubmit() {
	const submitOrder = document.querySelector("#checkoutSidebar #submit-order");
	if (!submitOrder) return;

	submitOrder.addEventListener("click", function () {
		const emailInput = getCheckoutEmailInput();
		const email = emailInput?.value?.trim();
		if (!email) return;
		try {
			localStorage.setItem(CHECKOUT_EMAIL_STORAGE_KEY, email);
		} catch (error) {
			console.error("Failed to store checkout email:", error);
		}
	});
}

function getThankYouOrderNumber() {
	const orderNumberEl = document.querySelector('[data-testid="orderNumber"]');
	return orderNumberEl?.textContent?.trim() || "";
}

function getStoredCheckoutEmail() {
	try {
		return localStorage.getItem(CHECKOUT_EMAIL_STORAGE_KEY) || "";
	} catch (error) {
		return "";
	}
}

function writeTallyParamsToPageUrl(orderEmail, orderId) {
	const pageUrl = new URL(location.href);
	if (orderEmail) pageUrl.searchParams.set(TALLY_HIDDEN_ORDER_EMAIL, orderEmail);
	if (orderId) pageUrl.searchParams.set(TALLY_HIDDEN_ORDER_ID, orderId);
	history.replaceState(null, "", pageUrl);
}

function appendHiddenFieldsToEmbedUrl(urlString, orderEmail, orderId) {
	const url = new URL(urlString, location.origin);
	if (orderEmail) url.searchParams.set(TALLY_HIDDEN_ORDER_EMAIL, orderEmail);
	if (orderId) url.searchParams.set(TALLY_HIDDEN_ORDER_ID, orderId);
	return url.toString();
}

function isThankYouTallyEmbed(urlString) {
	const formId = getThankYouTallyFormId();
	return typeof urlString === "string" && Boolean(formId) && urlString.includes("tally.so") && urlString.includes(formId);
}

function prepareTallyIframes(orderEmail, orderId) {
	document.querySelectorAll('iframe[data-tally-src], iframe[src*="tally.so"]').forEach((iframe) => {
		const dataSrc = iframe.getAttribute("data-tally-src");
		const src = iframe.getAttribute("src");
		const base = dataSrc || src;
		if (!isThankYouTallyEmbed(base)) return;

		const nextSrc = appendHiddenFieldsToEmbedUrl(base, orderEmail, orderId);
		iframe.setAttribute("data-tally-src", nextSrc);
		if (src) iframe.setAttribute("src", nextSrc);
	});
}

function passCheckoutDataToTally() {
	if (!getThankYouTallyFormId()) {
		if (document.body.classList.contains("admin-logged")) {
			console.warn("Nastavte TALLY_THANK_YOU_FORM_ID");
		}
		return;
	}

	const pageParams = new URLSearchParams(location.search);
	const orderEmail = getStoredCheckoutEmail() || pageParams.get(TALLY_HIDDEN_ORDER_EMAIL) || "";
	const orderId = getThankYouOrderNumber() || pageParams.get(TALLY_HIDDEN_ORDER_ID) || "";
	if (!orderEmail && !orderId) return;

	try {
		writeTallyParamsToPageUrl(orderEmail, orderId);
	} catch (error) {
		console.error("Failed to update thank-you URL for Tally:", error);
		return;
	}

	prepareTallyIframes(orderEmail, orderId);
}

window.passCheckoutDataToTally = passCheckoutDataToTally;
